/**
 * CapacityPicker.tsx
 * Feature: 155 - Telemetry Pipeline Provisioning & Management
 *
 * Two-panel capacity selector. Available capacities on the left,
 * selected capacities on the right. Supports:
 *   - Click to move individual items
 *   - HTML5 drag-and-drop between panels
 *   - Multi-select (Shift / Ctrl+click) + Assign button
 *   - Search filter on available panel
 *   - Add All / Remove All shortcuts
 */

import React, { useState, useRef, useCallback } from 'react';
import { Search, X, ChevronRight, ChevronLeft, ChevronsRight, ChevronsLeft } from 'lucide-react';
import { useCapacityList } from '../../services/operations/capacitiesService';
import type { Capacity } from '../../types/operations';

// ── Type badge ────────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  Fabric: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  PowerBI: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  MsManaged: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  Unknown: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};

function TypeBadge({ type }: { type: string }) {
  return (
    <span
      className={`ml-2 inline-flex flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${TYPE_COLORS[type] ?? TYPE_COLORS.Unknown}`}
    >
      {type}
    </span>
  );
}

// ── Single item row ───────────────────────────────────────────────────────────

interface ItemRowProps {
  capacity: Capacity;
  selected: boolean;
  onClick: (id: string, e: React.MouseEvent) => void;
  onDoubleClick: (id: string) => void;
  draggable: boolean;
  onDragStart: (e: React.DragEvent, id: string, source: 'available' | 'selected') => void;
  source: 'available' | 'selected';
}

function ItemRow({
  capacity,
  selected,
  onClick,
  onDoubleClick,
  draggable,
  onDragStart,
  source,
}: ItemRowProps) {
  return (
    <div
      draggable={draggable}
      onDragStart={(e) => onDragStart(e, capacity.capacity_id, source)}
      onClick={(e) => onClick(capacity.capacity_id, e)}
      onDoubleClick={() => onDoubleClick(capacity.capacity_id)}
      className={`flex cursor-pointer select-none items-center rounded px-2 py-1.5 text-sm transition-colors ${
        selected
          ? 'bg-[var(--color-accent)] text-white'
          : 'text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)]'
      }`}
    >
      <span className="min-w-0 flex-1 truncate">{capacity.capacity_name}</span>
      <TypeBadge type={capacity.capacity_type} />
    </div>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────────────

interface PanelProps {
  title: string;
  count: number;
  items: Capacity[];
  highlightedIds: Set<string>;
  onItemClick: (id: string, e: React.MouseEvent) => void;
  onItemDoubleClick: (id: string) => void;
  onDragStart: (e: React.DragEvent, id: string, source: 'available' | 'selected') => void;
  onDrop: (e: React.DragEvent, target: 'available' | 'selected') => void;
  source: 'available' | 'selected';
  emptyText: string;
  headerRight?: React.ReactNode;
  isDropTarget: boolean;
  onDragEnter: () => void;
  onDragLeave: () => void;
}

function Panel({
  title,
  count,
  items,
  highlightedIds,
  onItemClick,
  onItemDoubleClick,
  onDragStart,
  onDrop,
  source,
  emptyText,
  headerRight,
  isDropTarget,
  onDragEnter,
  onDragLeave,
}: PanelProps) {
  return (
    <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-[var(--color-border)]">
      {/* Panel header */}
      <div className="flex items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
          {title}
        </span>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-[var(--color-bg-subtle)] px-1.5 py-0.5 text-xs font-medium text-[var(--color-text-secondary)]">
            {count}
          </span>
          {headerRight}
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => onDrop(e, source)}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        className={`overflow-y-auto p-1 transition-colors ${
          isDropTarget ? 'bg-blue-50 dark:bg-blue-900/10' : 'bg-[var(--color-bg-card)]'
        }`}
        style={{ minHeight: 180, maxHeight: 260 }}
      >
        {items.length === 0 ? (
          <p className="p-4 text-center text-xs text-[var(--color-text-secondary)]">{emptyText}</p>
        ) : (
          items.map((cap) => (
            <ItemRow
              key={cap.capacity_id}
              capacity={cap}
              selected={highlightedIds.has(cap.capacity_id)}
              onClick={onItemClick}
              onDoubleClick={onItemDoubleClick}
              draggable
              onDragStart={onDragStart}
              source={source}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface CapacityPickerProps {
  /** Currently selected capacity IDs (internal dbo.capacities.id) */
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /** Capacity IDs to hide from the available panel (already assigned to this SMA) */
  excludedIds?: Set<string>;
}

export function CapacityPicker({ value, onChange, disabled = false, excludedIds = new Set() }: CapacityPickerProps) {
  const { data, isLoading } = useCapacityList({ pageSize: 500, includeInactive: true });
  const allCapacities: Capacity[] = data?.data ?? [];

  const [search, setSearch] = useState('');
  // IDs highlighted (multi-selected) in each panel
  const [availHighlight, setAvailHighlight] = useState<Set<string>>(new Set());
  const [selHighlight, setSelHighlight] = useState<Set<string>>(new Set());
  // Which panel the drag is hovering over
  const [dropTarget, setDropTarget] = useState<'available' | 'selected' | null>(null);
  // Track last-clicked index for shift-select
  const lastClickedAvail = useRef<string | null>(null);
  const lastClickedSel = useRef<string | null>(null);

  const selectedSet = new Set(value);

  // Only Fabric F-SKU capacities support Eventstreams. PowerBI, MsManaged, and
  // Unknown types cannot host Eventstream pipelines — exclude from both panels.
  const eligibleCapacities = allCapacities.filter((c) => c.capacity_type === 'Fabric');

  // Split into available vs selected
  const available = eligibleCapacities.filter(
    (c) =>
      !selectedSet.has(c.capacity_id) &&
      !excludedIds.has(c.capacity_id.toLowerCase()) &&
      c.capacity_name.toLowerCase().includes(search.toLowerCase()),
  );
  const selected = eligibleCapacities.filter((c) => selectedSet.has(c.capacity_id));

  // ── Move helpers ─────────────────────────────────────────────────────────────

  const addIds = useCallback(
    (ids: string[]) => {
      const current = new Set(value);
      const next = [...value, ...ids.filter((id) => !current.has(id))];
      onChange(next);
    },
    [value, onChange],
  );

  const removeIds = useCallback(
    (ids: string[]) => {
      const removing = new Set(ids);
      onChange(value.filter((id) => !removing.has(id)));
    },
    [value, onChange],
  );

  // ── Single-click (with shift/ctrl multi-select) ───────────────────────────────

  function handleAvailClick(id: string, e: React.MouseEvent) {
    if (disabled) return;
    if (e.shiftKey && lastClickedAvail.current) {
      const idx1 = available.findIndex((c) => c.capacity_id === lastClickedAvail.current);
      const idx2 = available.findIndex((c) => c.capacity_id === id);
      const [lo, hi] = [Math.min(idx1, idx2), Math.max(idx1, idx2)];
      const range = new Set(available.slice(lo, hi + 1).map((c) => c.capacity_id));
      setAvailHighlight((prev) => new Set([...prev, ...range]));
    } else if (e.ctrlKey || e.metaKey) {
      setAvailHighlight((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    } else {
      setAvailHighlight(new Set([id]));
    }
    lastClickedAvail.current = id;
  }

  function handleSelClick(id: string, e: React.MouseEvent) {
    if (disabled) return;
    if (e.shiftKey && lastClickedSel.current) {
      const idx1 = selected.findIndex((c) => c.capacity_id === lastClickedSel.current);
      const idx2 = selected.findIndex((c) => c.capacity_id === id);
      const [lo, hi] = [Math.min(idx1, idx2), Math.max(idx1, idx2)];
      const range = new Set(selected.slice(lo, hi + 1).map((c) => c.capacity_id));
      setSelHighlight((prev) => new Set([...prev, ...range]));
    } else if (e.ctrlKey || e.metaKey) {
      setSelHighlight((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    } else {
      setSelHighlight(new Set([id]));
    }
    lastClickedSel.current = id;
  }

  // ── Double-click: immediately move ───────────────────────────────────────────

  function handleAvailDoubleClick(id: string) {
    if (disabled) return;
    addIds([id]);
    setAvailHighlight(new Set());
  }

  function handleSelDoubleClick(id: string) {
    if (disabled) return;
    removeIds([id]);
    setSelHighlight(new Set());
  }

  // ── Assign / remove highlighted ───────────────────────────────────────────────

  function assignHighlighted() {
    if (disabled || availHighlight.size === 0) return;
    addIds([...availHighlight]);
    setAvailHighlight(new Set());
  }

  function removeHighlighted() {
    if (disabled || selHighlight.size === 0) return;
    removeIds([...selHighlight]);
    setSelHighlight(new Set());
  }

  // ── Drag and drop ─────────────────────────────────────────────────────────────

  function handleDragStart(e: React.DragEvent, id: string, source: 'available' | 'selected') {
    if (disabled) return;
    e.dataTransfer.setData('capacityId', id);
    e.dataTransfer.setData('source', source);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDrop(e: React.DragEvent, target: 'available' | 'selected') {
    e.preventDefault();
    setDropTarget(null);
    if (disabled) return;
    const id = e.dataTransfer.getData('capacityId');
    const source = e.dataTransfer.getData('source') as 'available' | 'selected';
    if (!id || source === target) return;
    if (target === 'selected') addIds([id]);
    else removeIds([id]);
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-[var(--color-text-secondary)]">
        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-accent)]" />
        Loading capacities…
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${disabled ? 'pointer-events-none opacity-50' : ''}`}>
      <div className="flex gap-3" style={{ minHeight: 280 }}>
        {/* ── Available panel ── */}
        <div className="flex flex-1 flex-col gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-text-secondary)]" />
            <input
              type="text"
              placeholder="Search capacities…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] py-1.5 pl-8 pr-8 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)] focus:border-[var(--color-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Panel
            title="Available"
            count={available.length}
            items={available}
            highlightedIds={availHighlight}
            onItemClick={handleAvailClick}
            onItemDoubleClick={handleAvailDoubleClick}
            onDragStart={handleDragStart}
            onDrop={handleDrop}
            source="available"
            emptyText={
              search
                ? 'No capacities match your search.'
                : allCapacities.length === 0
                ? 'No capacities found. Run the collection job to populate capacity data.'
                : eligibleCapacities.length === 0
                ? 'No Fabric F-SKU capacities found in this account.'
                : 'All capacities are already assigned to SMAs.'
            }
            isDropTarget={dropTarget === 'available'}
            onDragEnter={() => setDropTarget('available')}
            onDragLeave={() => setDropTarget(null)}
          />
        </div>

        {/* ── Center action buttons ── */}
        <div className="flex flex-col items-center justify-center gap-1.5 pt-10">
          <button
            onClick={assignHighlighted}
            disabled={availHighlight.size === 0}
            title={`Assign ${availHighlight.size} selected`}
            className="flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRight className="h-3.5 w-3.5" />
            {availHighlight.size > 0 ? `Add ${availHighlight.size}` : 'Add'}
          </button>
          <button
            onClick={() => { addIds(available.map((c) => c.capacity_id)); setAvailHighlight(new Set()); }}
            disabled={available.length === 0}
            title="Add all visible capacities"
            className="flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronsRight className="h-3.5 w-3.5" />
            All
          </button>
          <div className="my-1 h-px w-8 bg-[var(--color-border)]" />
          <button
            onClick={removeHighlighted}
            disabled={selHighlight.size === 0}
            title={`Remove ${selHighlight.size} selected`}
            className="flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            {selHighlight.size > 0 ? `Remove ${selHighlight.size}` : 'Remove'}
          </button>
          <button
            onClick={() => { onChange([]); setSelHighlight(new Set()); }}
            disabled={selected.length === 0}
            title="Remove all capacities"
            className="flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronsLeft className="h-3.5 w-3.5" />
            Clear
          </button>
        </div>

        {/* ── Selected panel ── */}
        <div className="flex flex-1 flex-col gap-2">
          <div className="py-1.5 text-xs text-transparent">spacer</div>
          <Panel
            title="Selected"
            count={selected.length}
            items={selected}
            highlightedIds={selHighlight}
            onItemClick={handleSelClick}
            onItemDoubleClick={handleSelDoubleClick}
            onDragStart={handleDragStart}
            onDrop={handleDrop}
            source="selected"
            emptyText="Drag capacities here or click Add."
            isDropTarget={dropTarget === 'selected'}
            onDragEnter={() => setDropTarget('selected')}
            onDragLeave={() => setDropTarget(null)}
          />
        </div>
      </div>

      <p className="text-xs text-[var(--color-text-secondary)]">
        Click to highlight · Double-click to move · Shift/Ctrl+click for multi-select · Drag between panels
      </p>
    </div>
  );
}
